import React from 'react';
import { toast } from 'react-toastify';

// Components
import { SuccessToast } from '../components/toasts/SuccessToast';
import { ErrorToast } from '../components/toasts/ErrorToast';
import { FileSavedToast } from '../components/toasts/FileSavedToast';
import { DirectionChangeToast } from '../components/toasts/DirectionChangeToast';

// Utils
import { toastFunctionConfig } from '../components/toasts/MainToast/utils';
import i18n from '../i18n';

/**
 * `toastId` (opcional): el mismo aviso dos veces seguidas sale UNA vez (p. ej.
 * dos peticiones iguales en el mismo instante, QA de «Mover tramos» 15-oct).
 */
export const showSuccessToast = (
  message: string,
  options?: { toastId?: string }
) => {
  toast(<SuccessToast text={message} />, {
    ...toastFunctionConfig,
    autoClose: 4000,
    ...(options?.toastId ? { toastId: options.toastId } : {}),
  });
};

/**
 * Aviso de fichero guardado, con la opción de abrirlo.
 *
 * Solo se usa dentro de la app: en el navegador la descarga ya la anuncia el
 * propio navegador. Vive más tiempo que un toast normal porque hay que darle
 * al operario ocasión de pulsar "Ver" con la tablet en la mano.
 */
export const showFileSavedToast = (filename: string, onOpen?: () => void) => {
  toast(<FileSavedToast filename={filename} onOpen={onOpen} />, {
    ...toastFunctionConfig,
    autoClose: 8000,
  });
};

/**
 * «Estás en la C-25z: se ha añadido al recorrido.» (2026-09-29)
 *
 * Lo lanza el cronómetro cuando la patrulla sigue por una vía de su concesión
 * que el recorrido no declaraba (`useAutoAddSupportRoad`). Es INFORMATIVO, sin
 * acción: por eso sí se cierra solo —la regla WCAG 2.2.1 que obliga al aviso
 * de cambio de sentido a quedarse es para toasts con controles—. Ocho
 * segundos y no cuatro: conduciendo, cuatro es un parpadeo.
 *
 * Con id por vía: dos avisos de la misma no se apilan.
 */
export const showSupportRoadAddedToast = (roadName: string) => {
  toast(
    <SuccessToast
      text={i18n.t('pages:surveillance.supportRoadAutoAdded', {
        road: roadName,
      })}
    />,
    {
      ...toastFunctionConfig,
      autoClose: 8000,
      toastId: `support-road-added:${roadName}`,
    }
  );
};

/**
 * «Se ha descartado el borrador At.… que dejaste a medias».
 *
 * Al empezar una básica nueva se borra el borrador que ESTA pestaña dejó a
 * medias (`discardPartialActionDraft`). Sin aviso, el operario no sabía qué
 * había pasado con él (decisión del usuario, 2026-10-09). Ocho segundos y no
 * cuatro: en carretera, cuatro es un parpadeo. Con id por borrador: no se
 * repite.
 */
export const showPreviousDraftDiscardedToast = (roid: string) => {
  toast(
    <SuccessToast
      text={i18n.t('toasts:info.previousDraftDiscarded', { roid })}
    />,
    {
      ...toastFunctionConfig,
      autoClose: 8000,
      toastId: `previous-draft-discarded:${roid}`,
    }
  );
};

/**
 * Id fijo del aviso de cambio de sentido.
 *
 * Con id, dos detecciones seguidas ACTUALIZAN el mismo aviso en vez de apilar
 * dos, y quien lo lanzó puede cerrarlo desde fuera (`dismissDirectionChangeToast`).
 */
export const DIRECTION_CHANGE_TOAST_ID = 'direction-change';

/**
 * «¿Has cambiado de sentido?».
 *
 * ── Por qué NO se autocierra ───────────────────────────────────────────────
 *
 * Dos razones independientes que apuntan al mismo sitio:
 *
 *  1. Ergonomía: cuatro segundos, con guantes y conduciendo, no es una
 *     pregunta — es un parpadeo.
 *  2. Norma: un toast que se autocierra LLEVANDO UNA ACCIÓN falla WCAG 2.2.1
 *     (Timing Adjustable), porque el usuario no puede alcanzar el control
 *     antes de que se vaya.
 *
 * Lo cierra el código: cuando el operario contesta, cuando el detector lo
 * registra por su cuenta, o cuando se sale de la zona.
 *
 * 🔴 Y por eso `closeOnClick` y `draggable` van a `false`, contra lo que trae
 * el contenedor global: en tablet un toque perdido o un roce lateral lo
 * tirarían. Tirarlo no corrompe nada —equivale a ignorarlo, y entonces el
 * detector lo registra solo— pero deja al operario sin poder decir «No», que
 * es la ÚNICA salida que no puede reproducirse sola.
 *
 * `role: 'status'` y no `alert`: es educado, no urgente. No interrumpe al
 * lector de pantalla ni le roba el foco a quien esté tecleando en marcha; con
 * react-toastify v11 se alcanza a demanda con `Alt+T`.
 */
export const showDirectionChangeToast = (
  onYes: () => void,
  onNo: () => void
) => {
  toast(<DirectionChangeToast onYes={onYes} onNo={onNo} />, {
    ...toastFunctionConfig,
    toastId: DIRECTION_CHANGE_TOAST_ID,
    autoClose: false,
    closeOnClick: false,
    draggable: false,
    role: 'status',
    ariaLabel: i18n.t('modals:timerOverlay.directionChangeQuestion'),
  });
};

/**
 * Retira el aviso sin responderlo.
 *
 * 🔴 Cerrar NO es decir «No». Quien llame aquí NO debe llamar además a
 * `dismiss()` del detector: eso rechazaría el extremo y callaría la detección
 * durante 1 km sin que nadie lo haya decidido — y no se notaría, porque
 * parecería que «no ha detectado nada».
 */
export const dismissDirectionChangeToast = () => {
  toast.dismiss(DIRECTION_CHANGE_TOAST_ID);
};

/**
 * `toastId` opcional: con él, react-toastify no pinta un segundo aviso igual
 * mientras el primero sigue en pantalla. Para avisos que puede lanzar más de
 * un ciclo a la vez (ver `useGpsCaptureGuardian`).
 */
export const showErrorToast = (
  message: string,
  options?: { toastId?: string; autoClose?: number | false }
) => {
  toast(<ErrorToast text={message} />, {
    ...toastFunctionConfig,
    autoClose: options?.autoClose ?? 6000,
    ...(options?.toastId ? { toastId: options.toastId } : {}),
  });
};

export const SESSION_ENDED_TOAST_ID = 'session-ended';

/**
 * «Tu sesión ha caducado…» en el login (spec 2026-10-01 § 5.4; en toast y no
 * dentro de la tarjeta, a petición del usuario el 2026-10-09). No se cierra
 * solo: el operario tiene que poder leerlo con la tablet en la mano. Con id:
 * ni StrictMode ni un segundo render lo duplican.
 */
export const showSessionEndedToast = (message: string) => {
  toast(<ErrorToast text={message} />, {
    ...toastFunctionConfig,
    autoClose: false,
    toastId: SESSION_ENDED_TOAST_ID,
  });
};

/** Al entrar (el login se desmonta) el aviso deja de aplicar. */
export const dismissSessionEndedToast = () => {
  toast.dismiss(SESSION_ENDED_TOAST_ID);
};

type GraphQLErrorShape = {
  message?: string;
  extensions?: {
    code?: string;
    status?: number;
    originalError?: { code?: string; statusCode?: number };
  };
};

/**
 * Toast de error de backend — doctrina 2026-07-17 (decidida con el usuario):
 * ESCALERA de resolución, de mejor a peor información:
 *
 *  1. CÓDIGO ESTABLE → i18n. Las excepciones de negocio llevan un `code`
 *     (p.ej. `SR_TIMER_ACTIVE_ELSEWHERE`, ver rois-back
 *     `utils/business-error.helper.ts`) que se traduce con la key
 *     `errors:backend.<code>` (ca/es/en). El contrato es el código: el
 *     backend puede reescribir su mensaje sin romper el front.
 *  2. NEGOCIO SIN CÓDIGO (4xx) → mensaje crudo del backend. Errores de
 *     negocio aún no migrados a código: mejor informar (aunque salga en
 *     castellano) que ocultar el motivo.
 *  3. TÉCNICO (5xx / red / sin mensaje) → fallback genérico i18n. NUNCA
 *     enseñar al usuario "Internal server error" ni errores de máquina.
 */
export const showBackendErrorToast = (
  error: unknown,
  fallback: string,
  options?: { toastId?: string }
) => {
  const gqlError = (error as { graphQLErrors?: GraphQLErrorShape[] })
    ?.graphQLErrors?.[0];
  const ext = gqlError?.extensions;

  // 1. Código estable → traducción del front.
  const code = ext?.originalError?.code ?? ext?.code;
  if (code && i18n.exists(`errors:backend.${code}`)) {
    showErrorToast(i18n.t(`errors:backend.${code}`), options);
    return;
  }

  // 2. Error de negocio (4xx) sin código → mensaje crudo del backend.
  const status = ext?.status ?? ext?.originalError?.statusCode;
  const isBusiness =
    typeof status === 'number' && status >= 400 && status < 500;
  if (isBusiness && gqlError?.message) {
    showErrorToast(gqlError.message, options);
    return;
  }

  // 3. Técnico → genérico.
  showErrorToast(fallback, options);
};
